const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

// The backend's auth layer says why it answered 401 (TTT-80). Only these come from the auth
// layer at all — a route that 401s for its own reason (a wrong password on a destructive action)
// sends no code, so it is never renewed and never replayed.
const SIGN_IN_EXPIRED = 'sign_in_expired';
const AUTH_401_CODES = [SIGN_IN_EXPIRED, 'no_sign_in', 'sign_in_unknown_user'];

// What a person reads when the server could not renew their sign-in (#505). Their sign-in is most
// likely fine, so it says what happened and to try again — never "Your sign-in has expired".
const SERVER_PROBLEM_MESSAGE = 'Something went wrong on our side. Please try again.';

// One in-flight renewal, shared by every call that expired at the same moment. A dashboard fires
// several requests at once; without this they would each POST /auth/refresh, and that endpoint
// allows 100 an hour.
let renewal = null;

// Say "your sign-in is over" once per signed-in stretch, not once per failed call — otherwise a
// page with five parallel requests raises five redirects and five notices. Reset only by proof
// that the sign-in is live again: a successful renewal or a fresh login. A 2xx from some public
// listing proves nothing.
let expiryAnnounced = false;
const expiryListeners = new Set();

// Called when the sign-in is genuinely over: the access token expired AND renewing it failed.
// Returns an unsubscribe function.
export function onSignInExpired(listener) {
  expiryListeners.add(listener);
  return () => expiryListeners.delete(listener);
}

function announceSignInExpired() {
  if (expiryAnnounced) return;
  expiryAnnounced = true;
  for (const listener of expiryListeners) listener();
}

function send(method, path, body) {
  const options = {
    method,
    credentials: 'include',
    headers: {},
  };

  if (body && !(body instanceof FormData)) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  } else if (body) {
    options.body = body;
  }

  return fetch(`${API_BASE}/api${path}`, options);
}

// Resolves true only when the sign-in is demonstrably live again. A 500, a 502 from a rolling
// deploy, a 429, or no network at all are NOT "your sign-in is over" — reporting them as an
// expiry signs out a user whose 7-day refresh token is sitting there, perfectly valid.
function renewSignIn() {
  if (!renewal) {
    renewal = send('POST', '/auth/refresh')
      .then(res => {
        if (res.ok) {
          // Renewing proves the sign-in is live, so a later expiry may announce itself again.
          expiryAnnounced = false;
          return true;
        }
        if (res.status !== 401) {
          console.error(`[api] could not renew the sign-in: /auth/refresh answered ${res.status}`);
        }
        return res.status === 401 ? 'expired' : 'unavailable';
      })
      .catch(err => {
        console.error('[api] could not reach the server to renew the sign-in:', err);
        return 'unavailable';
      })
      .finally(() => { renewal = null; });
  }
  return renewal;
}

// Send a call, renewing an expired sign-in and replaying once. Resolves to the final response;
// `data` is its parsed JSON when this had to read the body (a 401), and absent otherwise, so a
// caller that wants the body as a file can still read it. `signInOver` is true when the call
// failed because the sign-in is over and renewing could not bring it back.
async function sendRenewing(method, path, body = null, canRenew = true) {
  const res = await send(method, path, body);
  if (res.status !== 401) return { res };
  const data = await res.json().catch(() => null);

  // A 401 from the auth layer is nearly always the 15-minute access token running out while the
  // user was still working. Renew and replay once so they never see it. The replay passes
  // canRenew=false, so a 401 renewing cannot fix falls through and is reported instead of looping.
  if (AUTH_401_CODES.includes(data?.code)) {
    if (canRenew && data.code === SIGN_IN_EXPIRED) {
      const renewed = await renewSignIn();
      if (renewed === true) return sendRenewing(method, path, body, false);
      // The server could not be reached or could not answer. Their sign-in may well be fine, so
      // let the caller report a server problem rather than throwing them out of the app. The
      // server's "Your sign-in has expired" would be untrue here, so it is dropped (#505).
      if (renewed === 'unavailable') throw serverProblemError(res);
    }
    announceSignInExpired();
    return { res, data, signInOver: true };
  }
  return { res, data };
}

async function request(method, path, body = null) {
  const result = await sendRenewing(method, path, body);
  const { res } = result;
  const data = 'data' in result ? result.data : await res.json().catch(() => null);

  if (!res.ok) throw toError(res, data, result.signInOver);

  return data;
}

// How long the browser keeps a downloaded file's object URL. Revoking it in the same tick as the
// click cancels the download in some browsers.
const DOWNLOAD_URL_LIFETIME_MS = 10_000;

// GET a file and hand it to the browser as a download named `filename`. Renews an expired sign-in
// like every other call; a refusal throws the same Error with `.status` and `.data`.
async function download(path, filename) {
  const result = await sendRenewing('GET', path);
  const { res } = result;
  if (!res.ok) {
    const data = 'data' in result ? result.data : await res.json().catch(() => null);
    throw toError(res, data, result.signInOver);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_LIFETIME_MS);
}

function toError(res, data, signInOver = false) {
  const error = new Error(data?.message || res.statusText);
  error.status = res.status;
  error.data = data;
  if (signInOver) error.signInExpired = true;
  return error;
}

// The error for a call whose sign-in could not be renewed because the server failed (a 5xx, a 429
// or no network). It keeps the call's status but carries our try-again message, not the server's.
function serverProblemError(res) {
  const data = { message: SERVER_PROBLEM_MESSAGE };
  const error = toError(res, data);
  error.serverProblem = true;
  return error;
}

// True when a call failed because the server could not renew the sign-in. The person is most
// likely still signed in; the error's message says something went wrong and to try again.
export function isServerProblem(err) {
  return err?.serverProblem === true;
}

// True when a call failed because the sign-in is over (renewing it failed too). The app already
// sends the person to log in with its own notice (onSignInExpired), so the page that sent the call
// shows nothing: the server's own words ("Please log in to continue.") must not reach them (#502).
// A wrong password, a server problem during renewal or any other failure is false: those are the
// person's to read.
export function isSignInExpired(err) {
  return err?.signInExpired === true;
}

// Called by AuthContext when a login or MFA challenge succeeds: the sign-in is live again, so a
// later expiry is allowed to announce itself.
export function signInStarted() {
  expiryAnnounced = false;
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  patch: (path, body) => request('PATCH', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path, body) => request('DELETE', path, body),
  download: (path, filename) => download(path, filename),
};

// Resolve an API-served asset path (e.g. "/uploads/qr.png") against the API origin —
// needed in dev where the SPA (:5173) and API (:3001) are different origins.
export function assetUrl(u) {
  return u && u.startsWith('/') ? `${API_BASE}${u}` : u;
}
